import React, { useState, useMemo } from 'react';
import P from '@hero/ui-kit/typography/P';
import H from '@hero/ui-kit/typography/H';
import Tag from '@hero/ui-kit/components/Tag';
import Button from '@hero/ui-kit/inputs/Button';
import { formatDateTime, normalizeDateString } from '@hero/hero-utils/date';
import InlineLoader from '../../../../components/InlineLoader';
import QueryInlineError from '../../../../components/QueryInlineError';
import useContactEngagement from '../../api/useContactEngagement';
import type { DripEventType } from '../../types';
type Props = {
    profileId: number;
};
const PAGE_SIZE = 20;
const eventTagType = (eventType: DripEventType) => {
    switch (eventType) {
        case 'EMAIL_DELIVERED':
        case 'SMS_DELIVERED':
        case 'CONVERTED':
            return 'success' as const;
        case 'EMAIL_BOUNCED':
        case 'EMAIL_SEND_FAILED':
        case 'SMS_FAILED':
        case 'COMPLAINT':
            return 'fail' as const;
        case 'UNSUBSCRIBED':
        case 'EXITED':
        case 'SMS_SKIPPED':
            return 'warn' as const;
        default:
            return 'primary' as const;
    }
};
const EngagementTimeline: React.FC<Props> = ({ profileId }) => {
    const [offset, setOffset] = useState(0);
    const params = useMemo(
        () => ({ profile_id: profileId, offset, limit: PAGE_SIZE }),
        [profileId, offset]
    );
    const { data: response, isLoading, error } = useContactEngagement(params);
    const events = response?.data?.events || [];
    const totalCount = response?.data?.total_count || 0;
    const hasMore = offset + PAGE_SIZE < totalCount;
    const hasPrev = offset > 0;
    return (
        <div
            style={{
                marginTop: '2.4rem'
            }}
        >
            <H
                role="h6"
                noDefaultMargin
                styles={{
                    marginBottom: '1.2rem'
                }}
            >
                Engagement Timeline
            </H>
            {isLoading ? (
                <InlineLoader />
            ) : error ? (
                <QueryInlineError error={error} />
            ) : events.length === 0 ? (
                <P noDefaultMargin>No engagement events.</P>
            ) : (
                <>
                    <P
                        noDefaultMargin
                        size="small"
                        styles={{
                            marginBottom: '1.2rem'
                        }}
                    >
                        Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, totalCount)} of{' '}
                        {totalCount}
                    </P>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            rowGap: '1.2rem'
                        }}
                    >
                        {events.map((event, idx) => (
                            <div
                                key={`${event.timestamp}-${idx}`}
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    columnGap: '1.6rem',
                                    padding: '1.2rem',
                                    borderBottom: '1px solid var(--mark-border)'
                                }}
                            >
                                <Tag type={eventTagType(event.event_type)} size="small">
                                    {event.event_type.replace(/_/g, ' ')}
                                </Tag>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        rowGap: '0.4rem',
                                        flex: 1
                                    }}
                                >
                                    <P noDefaultMargin size="small">
                                        {formatDateTime(normalizeDateString(event.timestamp))}
                                    </P>
                                    {(event.drip_name || event.blast_name) && (
                                        <P noDefaultMargin strong>
                                            {event.drip_name || event.blast_name}
                                        </P>
                                    )}
                                    {event.step_id != null && (
                                        <P noDefaultMargin size="small">
                                            Step {event.step_id}
                                            {event.variant ? ` · ${event.variant}` : ''}
                                        </P>
                                    )}
                                    {event.provider_message_id && (
                                        <P noDefaultMargin size="small">
                                            Message ID: {event.provider_message_id}
                                        </P>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            columnGap: '1.6rem',
                            marginTop: '1.6rem'
                        }}
                    >
                        {hasPrev && (
                            <Button
                                type="button"
                                variant="outlineSecondary"
                                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                                noDefaultMargin
                            >
                                Previous
                            </Button>
                        )}
                        {hasMore && (
                            <Button
                                type="button"
                                variant="outlineSecondary"
                                onClick={() => setOffset(offset + PAGE_SIZE)}
                                noDefaultMargin
                            >
                                Next
                            </Button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};
export default EngagementTimeline;
